import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { v4 } from '@rotra-air-ocean/rotranext-shared-code';
import { Plus, Trash2 } from 'lucide-react';

import { Stack } from '$layouts/v2/components/Stack.jsx';

const QUESTION_TYPES: v4.SystemOneQuestionSpec['type'][] = [
  'choice',
  'score',
  'noul',
];

function defaultQuestion(
  type: v4.SystemOneQuestionSpec['type'],
): v4.SystemOneQuestionSpec {
  switch (type) {
    case 'choice':
      return { type: 'choice', instructions: '', criteria: {} };
    case 'score':
      return { type: 'score', instructions: '', criteria: ['', ''] };
    case 'noul':
      return { type: 'noul', instructions: '' };
  }
}

/** Editor for a `choice` question's option name -> description map. */
function ChoiceCriteriaEditor({
  criteria,
  onChange,
  readOnly,
}: {
  criteria: Record<string, string>;
  onChange: (criteria: Record<string, string>) => void;
  readOnly: boolean;
}) {
  const entries = Object.entries(criteria);
  return (
    <Stack spacing={1}>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
        }}
      >
        Options the model can choose between.
      </Typography>
      {entries.map(([name, description], index) => (
        <Box
          key={index}
          sx={{
            display: 'grid',
            gridTemplateColumns: readOnly
              ? 'minmax(0, 1fr) minmax(0, 2fr)'
              : 'minmax(0, 1fr) minmax(0, 2fr) auto',
            gap: 1,
          }}
        >
          <TextField
            label="Option name"
            value={name}
            onChange={
              readOnly
                ? undefined
                : (event) => {
                    const next: Record<string, string> = {};
                    entries.forEach(([currentName, currentDescription], i) => {
                      next[i === index ? event.target.value : currentName] =
                        currentDescription;
                    });
                    onChange(next);
                  }
            }
            slotProps={{ htmlInput: { readOnly } }}
          />
          <TextField
            label="Description"
            value={description}
            onChange={
              readOnly
                ? undefined
                : (event) =>
                    onChange({ ...criteria, [name]: event.target.value })
            }
            slotProps={{ htmlInput: { readOnly } }}
          />
          {!readOnly && (
            <IconButton
              size="small"
              aria-label={`Remove ${name || 'option'}`}
              onClick={() => {
                const next = { ...criteria };
                delete next[name];
                onChange(next);
              }}
            >
              <Trash2 size={14} />
            </IconButton>
          )}
        </Box>
      ))}
      {!readOnly && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<Plus size={14} />}
          onClick={() => onChange({ ...criteria, '': '' })}
        >
          Add option
        </Button>
      )}
    </Stack>
  );
}

/** Editor for a `score` question's ordered level descriptions. */
function ScoreCriteriaEditor({
  criteria,
  onChange,
  readOnly,
}: {
  criteria: string[];
  onChange: (criteria: string[]) => void;
  readOnly: boolean;
}) {
  return (
    <Stack spacing={1}>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
        }}
      >
        Levels from the low end of the scale to the high end (at least two).
      </Typography>
      {criteria.map((level, index) => (
        <Box
          key={index}
          sx={{
            display: 'grid',
            gridTemplateColumns: readOnly ? '1fr' : 'minmax(0, 1fr) auto',
            gap: 1,
            alignItems: 'center',
          }}
        >
          <TextField
            label={`Level ${index}`}
            value={level}
            onChange={
              readOnly
                ? undefined
                : (event) =>
                    onChange(
                      criteria.map((current, i) =>
                        i === index ? event.target.value : current,
                      ),
                    )
            }
            slotProps={{ htmlInput: { readOnly } }}
          />
          {!readOnly && criteria.length > 2 && (
            <IconButton
              size="small"
              aria-label={`Remove level ${index}`}
              onClick={() => onChange(criteria.filter((_, i) => i !== index))}
            >
              <Trash2 size={14} />
            </IconButton>
          )}
        </Box>
      ))}
      {!readOnly && criteria.length < 10 && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<Plus size={14} />}
          onClick={() => onChange([...criteria, ''])}
        >
          Add level
        </Button>
      )}
    </Stack>
  );
}

/** Editor for a `noul` question's optional true/false descriptions. */
function NoulCriteriaEditor({
  criteria,
  onChange,
  readOnly,
}: {
  criteria: { true: string; false: string } | undefined;
  onChange: (criteria: { true: string; false: string } | undefined) => void;
  readOnly: boolean;
}) {
  if (!criteria) {
    return (
      <Button
        size="small"
        variant="outlined"
        disabled={readOnly}
        startIcon={<Plus size={14} />}
        onClick={() => onChange({ true: '', false: '' })}
      >
        Add yes/no descriptions
      </Button>
    );
  }
  return (
    <Stack spacing={1}>
      <TextField
        label="What counts as yes"
        value={criteria.true}
        onChange={
          readOnly
            ? undefined
            : (event) => onChange({ ...criteria, true: event.target.value })
        }
        slotProps={{ htmlInput: { readOnly } }}
      />
      <TextField
        label="What counts as no"
        value={criteria.false}
        onChange={
          readOnly
            ? undefined
            : (event) => onChange({ ...criteria, false: event.target.value })
        }
        slotProps={{ htmlInput: { readOnly } }}
      />
      {!readOnly && (
        <Button size="small" color="error" onClick={() => onChange(undefined)}>
          Remove yes/no descriptions
        </Button>
      )}
    </Stack>
  );
}

/** Question id input that keeps a local draft so an invalid (empty/duplicate) value can be typed through. */
function QuestionIdField({
  id,
  existingIds,
  onRename,
  readOnly,
}: {
  id: string;
  existingIds: string[];
  onRename: (newId: string) => void;
  readOnly: boolean;
}) {
  const [draft, setDraft] = useState(id);
  useEffect(() => setDraft(id), [id]);

  const isEmpty = draft === '';
  const isDuplicate = draft !== id && existingIds.includes(draft);
  const error = isEmpty
    ? 'Question id is required.'
    : isDuplicate
    ? 'Another question already uses this id.'
    : undefined;

  return (
    <TextField
      label="Question id"
      helperText={error ?? 'Answers are merged into context under this id.'}
      error={!!error}
      value={draft}
      onChange={
        readOnly
          ? undefined
          : (event) => {
              const value = event.target.value;
              setDraft(value);
              if (value && (value === id || !existingIds.includes(value))) {
                onRename(value);
              }
            }
      }
      onBlur={() => setDraft(id)}
      slotProps={{ htmlInput: { readOnly } }}
    />
  );
}

/**
 * Editor for a `systemOne` step's named Choice/Score/Noul questions. Each
 * answer is later merged into the workflow context under its question id.
 */
export function SystemOneQuestionsEditor({
  questions,
  onChange,
  readOnly = false,
}: {
  questions: Record<string, v4.SystemOneQuestionSpec>;
  onChange: (questions: Record<string, v4.SystemOneQuestionSpec>) => void;
  readOnly?: boolean;
}) {
  const entries = Object.entries(questions);

  const renameQuestion = (oldId: string, newId: string) => {
    if (!newId || (newId !== oldId && questions[newId])) return;
    const next: Record<string, v4.SystemOneQuestionSpec> = {};
    for (const [id, question] of entries) {
      next[id === oldId ? newId : id] = question;
    }
    onChange(next);
  };

  const updateQuestion = (
    id: string,
    patch: Partial<v4.SystemOneQuestionSpec>,
  ) => {
    onChange({
      ...questions,
      [id]: { ...questions[id], ...patch } as v4.SystemOneQuestionSpec,
    });
  };

  const changeType = (id: string, type: v4.SystemOneQuestionSpec['type']) => {
    const current = questions[id];
    onChange({
      ...questions,
      [id]: { ...defaultQuestion(type), instructions: current.instructions },
    });
  };

  const removeQuestion = (id: string) => {
    const next = { ...questions };
    delete next[id];
    onChange(next);
  };

  const addQuestion = () => {
    let id = 'question';
    let counter = 1;
    while (questions[id]) {
      id = `question_${counter}`;
      counter += 1;
    }
    onChange({ ...questions, [id]: defaultQuestion('noul') });
  };

  return (
    <Stack spacing={2}>
      {entries.length === 0 && (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          No questions configured yet. Each question is evaluated in parallel in
          a single call.
        </Typography>
      )}
      {entries.map(([id, question], index) => (
        <Box
          key={index}
          sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}
        >
          <Stack spacing={1.5}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: readOnly
                  ? 'minmax(0, 1fr) minmax(0, 1fr)'
                  : 'minmax(0, 1fr) minmax(0, 1fr) auto',
                gap: 1,
              }}
            >
              <QuestionIdField
                id={id}
                existingIds={Object.keys(questions)}
                onRename={(newId) => renameQuestion(id, newId)}
                readOnly={readOnly}
              />
              <TextField
                select
                label="Type"
                value={question.type}
                disabled={readOnly}
                onChange={(event) =>
                  changeType(
                    id,
                    event.target.value as v4.SystemOneQuestionSpec['type'],
                  )
                }
              >
                {QUESTION_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type === 'choice'
                      ? 'Choice — pick one option'
                      : type === 'score'
                      ? 'Score — position on a scale'
                      : 'Noul — yes/no'}
                  </MenuItem>
                ))}
              </TextField>
              {!readOnly && (
                <IconButton
                  size="small"
                  aria-label={`Remove ${id}`}
                  onClick={() => removeQuestion(id)}
                >
                  <Trash2 size={14} />
                </IconButton>
              )}
            </Box>
            <TextField
              label="Instructions"
              helperText="The question the model answers."
              value={question.instructions}
              multiline
              onChange={
                readOnly
                  ? undefined
                  : (event) =>
                      updateQuestion(id, { instructions: event.target.value })
              }
              slotProps={{ htmlInput: { readOnly } }}
            />
            {question.type === 'choice' && (
              <ChoiceCriteriaEditor
                criteria={question.criteria}
                onChange={(criteria) => updateQuestion(id, { criteria })}
                readOnly={readOnly}
              />
            )}
            {question.type === 'score' && (
              <ScoreCriteriaEditor
                criteria={question.criteria}
                onChange={(criteria) => updateQuestion(id, { criteria })}
                readOnly={readOnly}
              />
            )}
            {question.type === 'noul' && (
              <NoulCriteriaEditor
                criteria={question.criteria}
                onChange={(criteria) => updateQuestion(id, { criteria })}
                readOnly={readOnly}
              />
            )}
          </Stack>
        </Box>
      ))}
      {!readOnly && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<Plus size={14} />}
          onClick={addQuestion}
        >
          Add question
        </Button>
      )}
    </Stack>
  );
}
